import { Button, Stack, Typography, LinearProgress, Box } from '@mui/material'
import { FormattedMessage } from 'react-intl'
import { useEffect, useState } from 'react'
import LocationPinBlueIcon from '../../resources/LocationPinBlue.svg'

interface SuccessPageProps {
  locationName: string
  licensePlate: string
  permitTime?: string
  onClose: () => void
}

export default function SuccessPage({
  locationName,
  licensePlate,
  permitTime,
  onClose
}: SuccessPageProps) {
  const [progress, setProgress] = useState(0)
  const MODAL_DURATION = 10000
  const UPDATE_INTERVAL = 100

  useEffect(() => {
    const startTime = Date.now()

    const timer = setInterval(() => {
      const elapsedTime = Date.now() - startTime
      const newProgress = (elapsedTime / MODAL_DURATION) * 100

      if (newProgress >= 100) {
        clearInterval(timer)
        onClose()
      } else {
        setProgress(newProgress)
      }
    }, UPDATE_INTERVAL)

    return () => clearInterval(timer)
  }, [])

  return (
    <Stack
      direction="column"
      spacing={4}
      sx={{
        bgcolor: 'background.paper',
        zIndex: 1000,
        alignItems: 'center',
        justifyContent: 'flex-start',
        marginTop: 10,
        minHeight: '100vh'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          width: { xs: 300, sm: 400 }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flexDirection: 'column',
            textAlign: 'center',
            width: '100%'
          }}
        >
          <Box
            component="img"
            alt="Moovy logo"
            src={LocationPinBlueIcon}
            sx={{
              mt: 1,
              alignSelf: 'center'
            }}
          />

          <Box sx={{ maxWidth: '600px' }}>
            <Typography
              variant="h1"
              sx={{
                color: 'primary.main',
                textTransform: 'uppercase'
              }}
            >
              {locationName}
            </Typography>

            <Typography
              variant="h2"
              aria-label="Parking info"
              sx={{
                color: 'text.primary',
                mt: 5
              }}
            >
              <FormattedMessage id="page.homePage.success.permitType" />
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: 'text.primary',
                mt: 1,
                fontSize: '1.2rem',
                marginTop: '18px'
              }}
            >
              <FormattedMessage id="page.homePage.success.permitTime" />
              <Typography
                component="p"
                sx={{
                  fontWeight: 'bold',
                  fontSize: '1.2rem'
                }}
              >
                {permitTime}
              </Typography>
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: 'text.primary',
                mt: 1,
                fontSize: '1.2rem',
                marginTop: '18px'
              }}
            >
              <FormattedMessage id="page.homePage.success.licensePlate" />
            </Typography>
            <Typography
              component="p"
              sx={{
                fontWeight: 'bold',
                fontSize: '1.2rem'
              }}
            >
              {licensePlate}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            height: '60px',
            marginTop: '4px',
            fontSize: '1.5rem',
            width: '200px'
          }}
        >
          <FormattedMessage id="page.homePage.success.okButton" />
        </Button>
      </Box>

      <Stack sx={{ alignItems: 'center', mt: 4 }}>
        <Box sx={{ width: '150px' }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 4,
              borderRadius: 1,
              '& .MuiLinearProgress-bar': {
                transition: 'none',
                borderRadius: 1
              }
            }}
          />
        </Box>
      </Stack>

      <Typography
        variant="body1"
        aria-label="Permit granted successfully"
        sx={{
          color: 'text.primary',
          mt: 1,
          fontSize: '1rem',
          marginTop: '18px'
        }}
      >
        <FormattedMessage id="page.homePage.success.clearingPage" />
      </Typography>
    </Stack>
  )
}
